import {useMutation} from '@apollo/react-hooks';
import type {FetchResult} from 'apollo-link';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    SharedEmailAddressAdd as AddSharedEmailAddressMutation,
    SharedEmailAddressAddVariables as AddSharedEmailAddressMutationVariables,
    SharedEmailAddressAddInput,
    GetSharedInboxConfig as GetSharedInboxConfigQuery,
} from 'nutshell-graphql-types';

import SHARED_EMAIL_ADDRESS_ADD from '../mutations/add-shared-email-address.graphql';
import GET_SHARED_INBOX_CONFIG from '../queries/get-shared-inbox-config.graphql';

interface UseAddSharedEmailAddressReturn {
    addSharedEmailAddress: (
        input: SharedEmailAddressAddInput
    ) => Promise<FetchResult<AddSharedEmailAddressMutation>>;
    isLoading: boolean;
    error: string | null | undefined;
}

export const useAddSharedEmailAddress = (): UseAddSharedEmailAddressReturn => {
    const [addSharedEmailAddress, {loading, error}] = useMutation<
        AddSharedEmailAddressMutation,
        AddSharedEmailAddressMutationVariables
    >(SHARED_EMAIL_ADDRESS_ADD);

    return {
        addSharedEmailAddress: (input: SharedEmailAddressAddInput) => {
            return addSharedEmailAddress({
                variables: {input},
                update: (cache, {data}) => {
                    const newFromAddress = data?.sharedEmailAddressAdd.fromAddress;
                    if (!newFromAddress) {
                        return;
                    }

                    const existingQueryResult = cache.readQuery<GetSharedInboxConfigQuery>({
                        query: GET_SHARED_INBOX_CONFIG,
                    });

                    if (!existingQueryResult?.sharedInboxConfig) {
                        return;
                    }

                    const addresses = existingQueryResult.sharedInboxConfig.sharedAddresses;
                    if (addresses.some((address) => address.id === newFromAddress.id)) {
                        return;
                    }

                    cache.writeQuery<GetSharedInboxConfigQuery>({
                        query: GET_SHARED_INBOX_CONFIG,
                        data: {
                            sharedInboxConfig: {
                                ...existingQueryResult.sharedInboxConfig,
                                sharedAddresses: [
                                    ...addresses,
                                    {
                                        ...newFromAddress,
                                        forwardingVerifiedTime: null,
                                    },
                                ],
                            },
                        },
                    });
                },
            });
        },
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
        isLoading: loading,
    };
};
